<template>
  <div class="system-runs">
    <!-- Statistics Cards -->
    <div class="row">
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-primary">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-chart-bar text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">Total Runs</p>
                <p class="card-title">{{ stats.summary.total_runs }}</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span class="text-white">
                <i class="fas fa-clock"></i> Last {{ filters.hours }} hours
              </span>
            </div>
          </div>
        </card>
      </div>
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-success">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-check-circle text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">Success Rate</p>
                <p class="card-title">{{ stats.summary.success_rate }}%</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span class="text-white">
                <i class="fas fa-thumbs-up"></i> {{ stats.summary.success_count }} successful
              </span>
            </div>
          </div>
        </card>
      </div>
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-danger">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-times-circle text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">Failed Runs</p>
                <p class="card-title">{{ stats.summary.failed_count }}</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span class="text-white">
                <i class="fas fa-exclamation-triangle"></i> Needs attention
              </span>
            </div>
          </div>
        </card>
      </div>
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-warning">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-stopwatch text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">Avg Duration</p>
                <p class="card-title">{{ formatDuration(stats.summary.avg_duration_seconds) }}</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span class="text-white">
                <i class="fas fa-spinner"></i> {{ stats.summary.running_count }} running now
              </span>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Command Statistics Table -->
    <div class="row">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">Command Statistics</h4>
          <div>
            <vue-good-table
              :columns="commandStatsColumns"
              :rows="stats.command_stats"
              :line-numbers="false"
              :search-options="{
                enabled: true,
                placeholder: 'Search commands',
              }"
              :pagination-options="{
                enabled: true,
                perPage: 15,
              }"
              styleClass="vgt-table nocturnal"
              theme="nocturnal"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'command_name'">
                  <router-link 
                    :to="{ name: 'CommandDetails', params: { commandName: props.row.command_name } }"
                    class="text-info clickable-command"
                  >
                    <strong>{{ props.row.command_name }}</strong>
                  </router-link>
                </span>
                <span v-else-if="props.column.field === 'success_rate'">
                  <span
                    :class="{
                      'text-success': props.row.success_rate >= 95,
                      'text-warning': props.row.success_rate >= 80 && props.row.success_rate < 95,
                      'text-danger': props.row.success_rate < 80
                    }"
                  >
                    {{ props.row.success_rate }}%
                  </span>
                </span>
                <span v-else-if="props.column.field === 'avg_duration'">
                  {{ formatDuration(props.row.avg_duration) }}
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
        </card>
      </div>
    </div>

    <!-- Duration Trends Section -->
    <div class="row">
      <div class="col-12">
        <card>
          <div slot="header" class="d-flex align-items-center">
            <h4 class="card-title mb-0">Duration Trends</h4>
            <span
              v-if="durationTrends.data && durationTrends.data.trend"
              class="badge badge-pill ml-2"
              :class="{
                'badge-success': durationTrends.data.trend.direction === 'improving',
                'badge-danger': durationTrends.data.trend.direction === 'degrading',
                'badge-secondary': durationTrends.data.trend.direction === 'stable',
              }"
            >
              {{ durationTrends.data.trend.direction === 'improving' ? '▲' : durationTrends.data.trend.direction === 'degrading' ? '▼' : '–' }}
              {{ durationTrends.data.trend.direction.charAt(0).toUpperCase() + durationTrends.data.trend.direction.slice(1) }}
              ({{ durationTrends.data.trend.change_percent > 0 ? '+' : '' }}{{ durationTrends.data.trend.change_percent }}%)
            </span>
            <span v-else-if="durationTrends.data && !durationTrends.data.trend" class="badge badge-pill badge-secondary ml-2">
              – Not enough data
            </span>
          </div>

          <!-- Controls -->
          <div class="row mb-4">
            <div class="col-md-4">
              <base-input label="Command">
                <select
                  v-model="durationTrends.selectedCommand"
                  class="form-control"
                  @change="fetchDurationTrends"
                >
                  <option value="">-- Select a command --</option>
                  <option
                    v-for="cmd in stats.command_stats"
                    :key="cmd.command_name"
                    :value="cmd.command_name"
                  >
                    {{ cmd.command_name }}
                  </option>
                </select>
              </base-input>
            </div>
            <div class="col-md-4">
              <base-input label="Granularity">
                <div class="btn-group w-100" role="group">
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="durationTrends.granularity === 'daily' ? 'btn-primary' : 'btn-secondary'"
                    @click="setTrendGranularity('daily')"
                  >Daily</button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="durationTrends.granularity === 'hourly' ? 'btn-primary' : 'btn-secondary'"
                    @click="setTrendGranularity('hourly')"
                  >Hourly</button>
                </div>
              </base-input>
            </div>
            <div class="col-md-4">
              <base-input label="Period">
                <div v-if="durationTrends.granularity === 'daily'" class="btn-group w-100" role="group">
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="durationTrends.days === 7 ? 'btn-primary' : 'btn-secondary'"
                    @click="setTrendDays(7)"
                  >7 Days</button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="durationTrends.days === 14 ? 'btn-primary' : 'btn-secondary'"
                    @click="setTrendDays(14)"
                  >14 Days</button>
                </div>
                <div v-else class="btn-group w-100" role="group">
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="durationTrends.hours === 24 ? 'btn-primary' : 'btn-secondary'"
                    @click="setTrendHours(24)"
                  >24h</button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="durationTrends.hours === 72 ? 'btn-primary' : 'btn-secondary'"
                    @click="setTrendHours(72)"
                  >72h</button>
                </div>
              </base-input>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="durationTrends.loading" class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x"></i>
            <p class="mt-2">Loading duration trends...</p>
          </div>

          <!-- Prompt -->
          <div v-else-if="!durationTrends.selectedCommand" class="text-center py-5 text-muted">
            <i class="fas fa-chart-line fa-3x mb-3" style="opacity:0.4"></i>
            <p>Select a command above to view duration trends</p>
          </div>

          <!-- Chart + table -->
          <div v-else-if="durationTrends.data && durationTrendsChartData">
            <line-chart
              :chart-data="durationTrendsChartData"
              :extra-options="durationTrendsChartOptions"
              :height="200"
            />

            <div class="text-right mt-3">
              <base-button
                type="link"
                size="sm"
                @click="durationTrends.showTable = !durationTrends.showTable"
              >
                <i :class="durationTrends.showTable ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                {{ durationTrends.showTable ? 'Hide' : 'Show' }} Data Table
              </base-button>
            </div>

            <div v-if="durationTrends.showTable" class="table-responsive mt-2">
              <table class="table table-dark table-sm">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Runs</th>
                    <th>Avg Duration</th>
                    <th>Min</th>
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in durationTrends.data.data" :key="row.period">
                    <td>{{ row.period }}</td>
                    <td>{{ row.run_count }}</td>
                    <td>{{ formatDuration(row.avg_duration) }}</td>
                    <td>{{ formatDuration(row.min_duration) }}</td>
                    <td>{{ formatDuration(row.max_duration) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Recent System Runs Table -->
    <div class="row">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">Recent System Runs</h4>
          <div>

    <!-- Filters -->
    <div class="row">
      <div class="col-12">
        <card>
          <div class="row">
            <div class="col-md-3">
              <base-input label="Status">
                <select
                  v-model="filters.status"
                  class="form-control"
                  @change="dataPoller.restart()"
                >
                  <option value="">All Statuses</option>
                  <option value="running">Running</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input
                label="Command Name"
                v-model="filters.base_name"
                placeholder="Filter by base name"
                @input="dataPoller.restart()"
              />
            </div>
            <div class="col-md-3">
              <base-input label="Time Period">
                <select
                  v-model="filters.hours"
                  class="form-control"
                  @change="dataPoller.restart()"
                >
                  <option :value="1">Last 1 Hour</option>
                  <option :value="6">Last 6 Hours</option>
                  <option :value="12">Last 12 Hours</option>
                  <option :value="24">Last 24 Hours</option>
                  <option :value="48">Last 48 Hours</option>
                  <option :value="168">Last 7 Days</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <base-button
                type="primary"
                @click="dataPoller.restart()"
                class="mb-3"
                block
              >
                <i class="fas fa-sync-alt"></i> Refresh
              </base-button>
            </div>
          </div>
        </card>
      </div>
    </div>
            <vue-good-table
              :columns="runsColumns"
              :rows="runs.data"
              :line-numbers="true"
              :search-options="{
                enabled: true,
                placeholder: 'Search runs',
              }"
              :pagination-options="{
                enabled: true,
                perPage: filters.per_page,
                perPageDropdownEnabled: false,
              }"
              :isLoading.sync="isLoading"
              styleClass="vgt-table nocturnal"
              theme="nocturnal"
              :totalRows="runs.total"
              mode="remote"
              @on-page-change="onPageChange"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'command_name'">
                  <code>{{ props.row.command_name }}</code>
                </span>
                <span v-else-if="props.column.field === 'status'">
                  <span
                    class="badge"
                    :class="{
                      'badge-success': props.row.status === 'success',
                      'badge-danger': props.row.status === 'failed',
                      'badge-warning': props.row.status === 'running'
                    }"
                  >
                    {{ props.row.status }}
                  </span>
                </span>
                <span v-else-if="props.column.field === 'started_at'">
                  {{ formatDate(props.row.started_at) }}
                </span>
                <span v-else-if="props.column.field === 'finished_at'">
                    {{ formatDate(props.row.finished_at) }}
                </span>
                <span v-else-if="props.column.field === 'duration_seconds'">
                  <span v-if="props.row.duration_seconds">
                    {{ formatDuration(props.row.duration_seconds) }}
                  </span>
                  <span v-else class="text-white">
                    <i class="fas fa-spinner spin"></i> Running
                  </span>
                </span>
                <span v-else-if="props.column.field === 'actions'">
                  <base-button
                    type="info"
                    size="sm"
                    @click="viewDetails(props.row.id)"
                  >
                    <i class="fas fa-eye"></i>
                  </base-button>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
        </card>
      </div>
    </div>

    <!-- Details Modal -->
    <modal :show.sync="showDetailsModal" body-classes="p-0" modal-classes="modal-lg">
      <card type="secondary" shadow header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <template>
          <div class="text-center mb-4">
            <h3>System Run Details</h3>
            <span
              class="badge badge-pill"
              :class="{
                'badge-success': selectedRun.status === 'success',
                'badge-danger': selectedRun.status === 'failed',
                'badge-warning': selectedRun.status === 'running'
              }"
            >
              {{ selectedRun.status }}
            </span>
          </div>

          <div v-if="selectedRun.id">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>ID:</strong></label>
                  <p>{{ selectedRun.id }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Base Name:</strong></label>
                  <p><code>{{ selectedRun.base_name }}</code></p>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label><strong>Command:</strong></label>
              <p><code>{{ selectedRun.command_name }}</code></p>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Started At:</strong></label>
                  <p>{{ formatDate(selectedRun.started_at) }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Finished At:</strong></label>
                  <p v-if="selectedRun.finished_at">{{ formatDate(selectedRun.finished_at) }}</p>
                  <p v-else class="text-white">Still running</p>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label><strong>Duration:</strong></label>
              <p v-if="selectedRun.duration_seconds">{{ formatDuration(selectedRun.duration_seconds) }}</p>
              <p v-else class="text-white">N/A</p>
            </div>

            <div class="form-group" v-if="selectedRun.metadata">
              <label><strong>Metadata:</strong></label>
              <pre class="bg-dark p-3 rounded">{{ JSON.stringify(selectedRun.metadata, null, 2) }}</pre>
            </div>

            <div class="form-group" v-if="selectedRun.error_message">
              <label class="text-danger"><strong>Error Message:</strong></label>
              <div class="alert alert-danger">{{ selectedRun.error_message }}</div>
            </div>

            <div class="form-group" v-if="selectedRun.exception_trace">
              <label class="text-danger"><strong>Exception Trace:</strong></label>
              <pre class="bg-dark p-3 rounded text-danger" style="max-height: 300px; overflow-y: auto;">{{ selectedRun.exception_trace }}</pre>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Created At:</strong></label>
                  <p>{{ formatDate(selectedRun.created_at) }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Updated At:</strong></label>
                  <p>{{ formatDate(selectedRun.updated_at) }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="text-center mt-4">
            <base-button type="secondary" @click="showDetailsModal = false">Close</base-button>
          </div>
        </template>
      </card>
    </modal>

    <!-- Recent Failures -->
    <div class="row" v-if="stats.recent_failures && stats.recent_failures.length > 0">
      <div class="col-12">
        <card type="danger">
          <h4 slot="header" class="card-title">
            <i class="fas fa-exclamation-circle"></i> Recent Failures
          </h4>
          <div class="table-responsive">
            <table class="table table-dark">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Command</th>
                  <th>Started At</th>
                  <th>Error Message</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="failure in stats.recent_failures" :key="failure.id">
                  <td>{{ failure.id }}</td>
                  <td><code>{{ failure.command_name }}</code></td>
                  <td>{{ formatDate(failure.started_at) }}</td>
                  <td class="text-danger">{{ failure.error_message || 'Unknown error' }}</td>
                  <td>
                    <base-button
                      type="info"
                      size="sm"
                      @click="viewDetails(failure.id)"
                    >
                      <i class="fas fa-eye"></i> View
                    </base-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Card, BaseButton, BaseInput, Modal } from '@/components';
import createPoller from '@/utils/createPoller';
import LineChart from '@/components/Charts/LineChart';

export default {
  name: 'SystemRuns',
  components: {
    VueGoodTable,
    Card,
    BaseButton,
    BaseInput,
    Modal,
    LineChart,
  },
  data() {
    return {
      isLoading: false,
      filters: {
        base_name: '',
        status: '',
        hours: 24,
        per_page: 15,
      },
      stats: {
        period_hours: 24,
        base_name_filter: null,
        summary: {
          total_runs: 0,
          success_count: 0,
          failed_count: 0,
          running_count: 0,
          success_rate: 0,
          avg_duration_seconds: 0,
        },
        recent_failures: [],
        command_stats: [],
      },
      runs: {
        current_page: 1,
        data: [],
        per_page: 15,
        total: 0,
      },
      commandStatsColumns: [
        {
          label: 'Command',
          field: 'command_name',
          sortable: true,
        },
        {
          label: 'Total Runs',
          field: 'total_runs',
          type: 'number',
          sortable: true,
        },
        {
          label: 'Success',
          field: 'success_count',
          type: 'number',
          sortable: true,
        },
        {
          label: 'Failed',
          field: 'failed_count',
          type: 'number',
          sortable: true,
        },
        {
          label: 'Success Rate',
          field: 'success_rate',
          type: 'number',
          sortable: true,
        },
        {
          label: 'Avg Duration',
          field: 'avg_duration',
          sortable: true,
        },
      ],
      runsColumns: [
        {
          label: 'Command',
          field: 'command_name',
          sortable: false,
        },
        {
          label: 'Status',
          field: 'status',
          sortable: false,
        },
        {
          label: 'Started At',
          field: 'started_at',
          sortable: false,
        },
        {
          label: 'Finished At',
          field: 'finished_at',
          sortable: false,
        },
        {
          label: 'Duration',
          field: 'duration_seconds',
          sortable: false,
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
        },
      ],
      showDetailsModal: false,
      selectedRun: {},
      durationTrends: {
        selectedCommand: '',
        granularity: 'daily',
        days: 7,
        hours: 24,
        loading: false,
        data: null,
        showTable: false,
      },
    };
  },
  created() {
    this.dataPoller = createPoller({
      fetchFn: () => this.fetchData(),
      interval: 30000,
      immediate: true,
      onError: (error) => {
        console.error('Error fetching system runs data:', error);
        this.$toast.error('Failed to fetch system runs data');
      },
    });
  },
  mounted() {
    this.dataPoller.start();
  },
  beforeDestroy() {
    this.dataPoller.destroy();
  },
  computed: {
    commandStatsWithSuccessRate() {
      return this.stats.command_stats.map(stat => ({
        ...stat,
        success_rate: stat.total_runs > 0
          ? ((stat.success_count / stat.total_runs) * 100).toFixed(2)
          : 0
      }));
    },
    durationTrendsChartData() {
      if (!this.durationTrends.data || !this.durationTrends.data.data.length) return null;
      const rows = this.durationTrends.data.data;
      return {
        labels: rows.map(r => r.period),
        datasets: [
          {
            label: 'Avg Duration (s)',
            data: rows.map(r => parseFloat(r.avg_duration)),
            borderColor: '#1d8cf8',
            pointBackgroundColor: '#1d8cf8',
            pointBorderColor: 'rgba(255,255,255,0)',
            pointHoverBackgroundColor: '#1d8cf8',
            pointBorderWidth: 20,
            pointHoverRadius: 4,
            pointHoverBorderWidth: 15,
            pointRadius: 4,
            fill: true,
          },
        ],
      };
    },
    durationTrendsChartOptions() {
      return {
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          backgroundColor: '#f5f5f5',
          titleFontColor: '#333',
          bodyFontColor: '#666',
          bodySpacing: 4,
          xPadding: 12,
          mode: 'nearest',
          intersect: false,
          position: 'nearest',
          callbacks: {
            label: (item) => ` ${this.formatDuration(item.yLabel)}`,
          },
        },
        responsive: true,
        scales: {
          yAxes: [{
            gridLines: {
              drawBorder: false,
              color: 'rgba(29,140,248,0.0)',
              zeroLineColor: 'transparent',
            },
            ticks: {
              suggestedMin: 0,
              padding: 20,
              fontColor: '#9a9a9a',
              callback: (value) => this.formatDuration(value),
            },
          }],
          xAxes: [{
            gridLines: {
              drawBorder: false,
              color: 'rgba(29,140,248,0.1)',
              zeroLineColor: 'transparent',
            },
            ticks: {
              padding: 20,
              fontColor: '#9a9a9a',
            },
          }],
        },
      };
    },
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        // Fetch statistics
        await this.fetchStats();
        // Fetch runs list
        await this.fetchRuns();
      } catch (error) {
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchStats() {
      const params = {
        hours: this.filters.hours,
      };
      if (this.filters.base_name) {
        params.base_name = this.filters.base_name;
      }

      const response = await axios.get('/system-runs/stats', { params });
      this.stats = response.data;

      // Calculate success rate for command stats
      this.stats.command_stats = this.stats.command_stats.map(stat => ({
        ...stat,
        success_rate: stat.total_runs > 0
          ? ((stat.success_count / stat.total_runs) * 100).toFixed(2)
          : 0
      }));
    },
    async fetchRuns(page = 1) {
      const params = {
        per_page: this.filters.per_page,
        hours: this.filters.hours,
        page: page,
      };
      if (this.filters.base_name) {
        params.base_name = this.filters.base_name;
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }

      const response = await axios.get('/system-runs', { params });
      this.runs = response.data;
    },
    async viewDetails(id) {
      try {
        this.isLoading = true;
        const response = await axios.get(`/system-runs/${id}`);
        this.selectedRun = response.data;
        this.showDetailsModal = true;
      } catch (error) {
        console.error('Error fetching run details:', error);
        this.$toast.error('Failed to fetch run details');
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(params) {
      this.fetchRuns(params.currentPage);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    async fetchDurationTrends() {
      if (!this.durationTrends.selectedCommand) return;
      this.durationTrends.loading = true;
      this.durationTrends.data = null;
      try {
        const params = {
          command_name: this.durationTrends.selectedCommand,
          granularity: this.durationTrends.granularity,
        };
        if (this.durationTrends.granularity === 'hourly') {
          params.hours = this.durationTrends.hours;
        } else {
          params.days = this.durationTrends.days;
        }
        const response = await axios.get('/system-runs/duration-trends', { params });
        this.durationTrends.data = response.data;
      } catch (error) {
        console.error('Error fetching duration trends:', error);
        this.$toast.error('Failed to fetch duration trends');
      } finally {
        this.durationTrends.loading = false;
      }
    },
    setTrendGranularity(g) {
      this.durationTrends.granularity = g;
      this.fetchDurationTrends();
    },
    setTrendDays(d) {
      this.durationTrends.days = d;
      this.fetchDurationTrends();
    },
    setTrendHours(h) {
      this.durationTrends.hours = h;
      this.fetchDurationTrends();
    },
    formatDuration(seconds) {
      if (!seconds || seconds === null) return 'N/A';
      const sec = parseFloat(seconds);
      if (sec < 60) {
        return `${sec.toFixed(2)}s`;
      } else if (sec < 3600) {
        const mins = Math.floor(sec / 60);
        const secs = (sec % 60).toFixed(0);
        return `${mins}m ${secs}s`;
      } else {
        const hours = Math.floor(sec / 3600);
        const mins = Math.floor((sec % 3600) / 60);
        return `${hours}h ${mins}m`;
      }
    },
  },
};
</script>

<style scoped>
/* Stats Cards */
.icon-big {
  font-size: 3em;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-big i {
  line-height: 1;
}

.numbers {
  text-align: right;
  font-size: 2em;
}

.numbers .card-category {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-bottom: 0;
  text-transform: uppercase;
  font-weight: 400;
}

.numbers .card-title {
  margin: 0;
  font-size: 1.7em;
  font-weight: 300;
  color: #ffffff;
}

.card-stats .card-footer {
  padding: 0.75rem 1.5rem;
  margin-top: 1rem;
}

.card-stats .card-footer hr {
  margin-top: 0;
  margin-bottom: 0.5rem;
}

.card-stats .card-footer .stats {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
}

.card-stats .card-footer .stats i {
  margin-right: 5px;
}

/* Gradient backgrounds */
.gradient-primary {
  background: linear-gradient(to bottom left, #1d8cf8, #3358f4, #1d8cf8);
}

.gradient-success {
  background: linear-gradient(to bottom left, #00bf9a, #00b09b, #00bf9a);
}

.gradient-danger {
  background: linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93);
}

.gradient-warning {
  background: linear-gradient(to bottom left, #ff8d72, #ff6491, #ff8d72);
}

/* Spin animation */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Clickable command link */
.clickable-command {
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-command:hover {
  text-decoration: underline;
  opacity: 0.8;
}

.clickable-command:active {
  transform: scale(0.98);
}
</style>
